<template>
  <div class="bg-secondary-9 rounded-lg container-border p-4">
    <div class="row">
      <div class="col-12 mb-1">
        <label class="color-gray-7 font-16 font-weight-bold">
          <i
            class="fas mr-1 rounded-circle bg-white p-1 shadow-sm"
            :class="data.titleClass"
          />
          Información de {{ capitalizedActionText }}:
        </label>
      </div>
      <div class="col-12 col-md-4">
        <validation-provider
          ref="determinationReason"
          v-slot="{ errors }"
          name="determinationReason"
          rules="required"
        >
          <custom-drop-down
            v-model="reasonSelected"
            :clearable="!data.isReadOnly"
            custom-error-msg="La razón es requerida."
            :disabled="data.isReadOnly"
            :error="errors[0]"
            fieldkey="id"
            fieldname="name"
            :label="`Razón de ${capitalizedActionText}:`"
            :options="data.reasons"
            placeholder="Seleccione"
            @input="onChangeReason"
          />
        </validation-provider>
      </div>
      <div v-if="isApproving" class="col-12 col-md-3">
        <validation-provider
          ref="approvalDate"
          v-slot="{ errors }"
          name="approvalDate"
          rules="required"
        >
          <label class="data-title mb-1">Fecha de Aprobación:</label>
          <custom-date-picker
            v-model="model.date"
            class-container="w-100"
            custom-error-msg="La fecha de aprobación es requerida."
            :disabled="data.isReadOnly"
            :error="errors[0]"
            format="dd/MMM./yyyy"
            :max-date="maxDate"
          />
        </validation-provider>
      </div>
      <div
        v-else-if="showDocument"
        class="col-12"
        :class="{
          'col-md-8': hasDocumentId,
          'col-md-4': !hasDocumentId,
        }"
      >
        <label class="data-title mb-0">Documento de respaldo:</label>
        <div v-if="data.isReadOnly">
          {{ model.attachment.name | empty }}
          <action-button
            class="mx-2 border-green document-button-icon"
            i="fas fa-file-download text-green mr-n1 pr-1"
            @click="downloadDocument"
          />
        </div>
        <validation-provider
          v-else
          v-slot="{ errors }"
          name="determinationDocument"
          :rules="hasDeterminationDocumentRequired ? 'required' : ''"
        >
          <custom-file-input
            ref="determinationDocument"
            v-model="model.attachment"
            accept=".pdf"
            accept-terms-modal-title="Certificación"
            accept-terms-text="Certifico que este documento que he subido al Sistema REMpro es copia fiel y exacta del original que pertenece a los récords de la Unidad Secretarial del Procedimiento de Querellas y Remedio Provisional (USPQRP) y que como proveedor de servicios seré custodio del mismo. Reconozco y acepto que este documento original me puede ser solicitado por la USPQRP en cualquier momento.(0423)"
            accept-terms-title="Acepto los términos y responsabilidades"
            accept-terms-validation-msg="Favor certifique el documento  que ha subido al sistema REMpro."
            allow-accept-terms
            custom-error-msg="El documento de respaldo es requerido."
            display-style="normal"
            :error="errors[0]"
            label-position="left"
            :on-download="onDownloadFile"
          />
        </validation-provider>
      </div>
      <div class="col-12 mt-3">
        <validation-provider
          v-slot="{ errors }"
          name="determinationComment"
          :rules="validationRulesForComment"
        >
          <label class="data-title mb-0">Comentario:</label>
          <custom-text-area
            v-model="model.comment"
            :count-classes="['float-right']"
            custom-error-msg="El comentario es requerido."
            :disabled="data.isReadOnly"
            :error="errors[0]"
            :maxlength="3000"
            show-char-count
          />
        </validation-provider>
      </div>
      <div v-if="!data.isReadOnly" class="col-12 mt-3">
        <div class="d-flex flex-row-reverse bd-highlight w-100 actions">
          <div class="bd-highlight ml-2">
            <action-button
              i="fas fa-save fa-lg"
              text="Guardar"
              variant="success"
              @click="save"
            />
          </div>
          <div class="mx-0">
            <action-button
              i="fas fa-times-circle"
              text="Cancelar"
              variant="outline-danger"
              @click="cancel"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import CustomDropDown from 'custom-drop-down';
import CustomTextArea from 'CustomTextArea';
import CustomDatePicker from 'custom-date-picker';
import ActionButton from 'ActionButton';
import CustomFileInput from 'CustomFileInput';

import { contractDocumentTypes } from '@/utils/constants/contractDocumentTypes.js';
import rpContractStatus from '@/utils/constants/rpContractStatus.js';
import rpContractDeterminationReasons from '@/utils/constants/rpContractDeterminationReasons.js';

export default {
  name: 'ContractDetermination',
  components: {
    CustomDropDown,
    CustomTextArea,
    CustomDatePicker,
    ActionButton,
    CustomFileInput,
  },
  props: {
    data: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    reasonSelected: null,
    maxDate: new Date(),
    document: {
      id: null,
      file: null,
    },
    model: {
      date: null,
      determinationReasonId: null,
      comment: null,
      documentTypeId: null,
      attachment: null,
    },
  }),
  methods: {
    cancel() {
      this.$emit('cancel', this.keepShowingValue);
    },
    save() {
      if (this.isCancelling && Boolean(this.model.attachment))
        this.model.documentTypeId = contractDocumentTypes.supplierChangeRequest;

      this.$emit('save', { ...this.model });
    },
    onChangeReason(item) {
      this.model.determinationReasonId = item?.id;
    },
    async onDownloadFile() {
      return new Promise();
    },
    downloadDocument() {
      const value = {
        documentId: this.data.contractStatus?.documentId,
        attachment: this.model.attachment,
      };
      this.$emit('download-document', value);
    },
  },
  computed: {
    keepShowingValue() {
      if (!this.data.contractStatus?.statusId) return false;
      return this.data.status !== this.data.contractStatus.statusId;
    },
    hasDocumentId() {
      return Boolean(this.data.contractStatus?.documentId);
    },
    isApproving() {
      return this.data.status === rpContractStatus.ApprovedContractInformation;
    },
    isDenying() {
      return this.data.status === rpContractStatus.DeniedContractInformation;
    },
    isCancelling() {
      return this.data.status === rpContractStatus.ContractCanceled;
    },
    capitalizedActionText() {
      let value = this.data.texts.action;
      if (!value) return '';
      return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    },
    hasDeterminationDocumentRequired() {
      return [
        rpContractDeterminationReasons.ReasonForSupplierChange,
        rpContractDeterminationReasons.DischargedService,
        rpContractDeterminationReasons.Absenteeism,
        rpContractDeterminationReasons.ProviderChange,
        rpContractDeterminationReasons.ContractBreach,
      ].includes(this.model.determinationReasonId);
    },
    showDocument() {
      return this.isCancelling && this.hasDeterminationDocumentRequired;
    },
    validationRulesForComment() {
      return this.data.status === rpContractStatus.ApprovedContractInformation
        ? ''
        : 'required';
    },
  },
  watch: {
    'data.status': {
      handler() {
        this.reasonSelected = null;
        this.model.date = null;
        this.model.determinationReasonId = null;
        this.model.comment = null;
        this.model.documentTypeId = null;
        this.model.attachment = null;
        this.$refs['determinationComment']?.reset();
        this.$refs['determinationReason']?.reset();
        this.$refs['approvalDate']?.reset();
      },
    },
    'data.contractStatus': {
      immediate: true,
      handler(value) {
        if (
          value &&
          value.determinationReasonId &&
          this.data.status === value.statusId
        ) {
          this.model.comment = value.comment;
          this.model.determinationReasonId = value.determinationReasonId;
          this.reasonSelected = {
            id: value.determinationReasonId,
            name: value.determinationReasonName,
          };
          this.model.date = value.date;
          this.model.attachment = { name: value.documentFileName };
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
.container-border {
  border-left: 5px solid #ff7058;
}
</style>
